import { ValidationErrors } from '@angular/forms';
import { IgniteResponse } from '@clubhub/ignite/models';
import { PropertyDetails } from '@clubhub/ignite/models/products/property-details';
import { ProductFeeType } from '@shared/constants';
import { groupBy, join2, mergedGroupBy } from '@shared/helpers';
import { AffordabilityRequest, BtlAffordabilityRequest, MortgageTypeValues, Product, RepaymentMethod } from '@shared/models';
import { DocumentPreferences } from '@shared/models/document-preferences.model';
import { Condition } from '@shared/models/rules-engine';
import { GridStateModel } from '@shared/models/simple-grid';
import {
  ApplicantCountry,
  BtlType,
  HolidayLetFrequency,
  MethodOfRepayment,
  ProductType,
  PropertyType,
  PurchaserType,
  RemortgageType,
  TaxStatusBtl,
  UkLocation,
} from './affordability-enums';
import { MortgageType } from './mortgage-type';

export enum LendingDocumentInformationType {
  Esis = 1,
  Illustration = 2,
}

export enum GeneratedLendingDocumentType {
  Esis = 1,
  Eor = 2,
  Illustration = 3,
}

export enum WhenPayableCustom {
  OnApplication = 1,
  OnCompletion = 2,
  OnOffer = 3,
  OnExchange = 4,
  UpFront = 5,
}

export enum WhenPayable {
  OnApplication = 1,
  OnCompletion = 2,
  OnOffer = 3,
  OnRedemption = 4,
}

export enum WhenRefundable {
  NotRefundable = 1,
  PriorToOffer = 2,
  PriorToValuation = 3,
  PriorToCompletion = 4,
}

export enum CriteriaType {
  Manual,
  Property,
  Intuitive,
}

export interface OutcomeViewModel {
  answerId: number;
  outcomeType: CriteriaType;
}

export interface HistoricalEsisEorDocument {
  documentId: string;
  createdDateUtc: string;
  daysRemaining: number;
  generatedTypes: GeneratedLendingDocumentType[];

  personId: string;
  clientForename: string;
  clientSurname: string;
  client2Forename: string | null;
  client2Surname: string | null;
  clientPostCode: string;
  lendingType: string;
}

export interface CostsToBePaidCustom {
  type: ProductFeeType;
  whenPayable: WhenPayableCustom;
  addToLoan: boolean;
  amountRefundable: number;
  whenRefundable: WhenRefundable;
  displayAs: string | null;
}

export interface CostsToBePaid {
  type: ProductFeeType;
  whenPayable: WhenPayable;
  addToLoan: boolean;
  amount: number;
}

export interface CostsToBePaidDetailed {
  type: ProductFeeType;
  amount: number;
  whenPayable: WhenPayableCustom;
  addToLoan: boolean;
  amountRefundable: number;
  whenRefundable: WhenRefundable;
  payableTo: string;
  displayAs: string | null;
}

export interface FlexibleFeatures {
  type: string;
  text: string;
  condition: Condition | null;
}

export interface IncentivesAdditionalFeatures {
  type: string;
  text: string;
  condition: Condition | null;
}

export interface EarlyRepaymentCharges {
  charge: string;
  amount: string;
}

export interface ESISInformation {
  introduction: {
    text: string;
  };
  lenderDetails: {
    lenderName: string;
    buildingName: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    telephone: string;
  };
  mainFeaturesOfLoan: {
    text: string;
  };
  costsToBePaid: CostsToBePaidCustom[];
  interestRatesOtherCosts: {
    text: string;
    costsNotKnownToLender: string;
  };
  frequencyNumberPayments: {
    text: string;
  };
  amountEachInstalment: {
    text: string;
  };
  additionalObligations: {
    text: string;
  };
  flexibleFeatures: FlexibleFeatures[];
  incentivesAdditionalFeatures: IncentivesAdditionalFeatures[];
  otherRightsOfBorrower: {
    reflectionPeriod: string;
    otherRights: string;
  };
  earlyRepaymentCharges: EarlyRepaymentCharges[];
  earlyRepaymentChargeTerms: {
    text: string;
  };
  nonCompliance: {
    text: string;
  };
  nonComplianceCi: {
    text: string;
  };
  nonComplianceIo: {
    text: string;
  };
  additionalInformation: {
    text: string;
  };
  linkedSavings: {
    text: string;
  };
  springBoard: {
    text: string;
  };
  sectionsEnabled: {
    lenderDetails: boolean;
    costsToBePaid: boolean;
    additionalObligations: boolean;
    flexibleFeatures: boolean;
    incentivesAdditionalFeatures: boolean;
    otherRightsOfBorrower: boolean;
    earlyRepaymentCharges: boolean;
    earlyRepaymentChargeTerms: boolean;
    nonCompliance: boolean;
    additionalInformation: boolean;
  };
}

export interface EsisEorGenerationRequest {
  originalRequestModel: unknown;
  details: {
    clientDetails: {
      clients: {
        forename: string;
        surname: string;
      }[];
      title: string;
      forename: string;
      surname: string;
      forename2: string | null;
      surname2: string | null;
      postcode: string;
      referenceNumber: string;
    };
    loanDetails: {
      mortgageType: MortgageType;
      productTypeExtended: ProductType;
      purchaserType: PurchaserType;
      propertyValue: number;
      loanAmount: number;
      ioLoanAmount: number;
      ltv: number;
      termYears: number;
      termMonths: number;
      repaymentMethod: RepaymentMethod;
      purchasePriceShare?: number;
      propertyType?: PropertyType;
    };
    isAdvised: boolean;
    complaintDetails: {
      contactPerson: string;
      addressLine1: string;
      addressLine2: string;
      town: string;
      county: string;
      postcode: string;
      telephone: string;
      email: string;
      maxComplaintHandlingTime: number;
    };
  };
  fees: {
    fees: CostsToBePaid[];
    customFees: CostsToBePaidDetailed[];
    feeWaiver: string;
    procuration: {
      rateId: number | null;
      procFeePaymentAmount: number;
      paymentRouteName: string;
      payableTo: string;
    };
  };
  eor: {
    resultsGrid: {
      rowCount: string;
      title: string;
      description: string;
    };
    recommendationReason: string;
    additionalInformation: string;
  };
  selectedProduct: Product;
  selectedProductRank: number;
  gridState: GridStateModel;
  selectedProductFilters: Record<'mortgageType' | 'initialPeriod' | 'productPreferences', string[]>;
  igniteLenders: string[];
  outcomes: OutcomeViewModel[];
  affordabilityRequest: AffordabilityRequest | BtlAffordabilityRequest;
  affordabilityResponse: IgniteResponse[];
  customTrueCostPeriod: string;
  productsSearchId: string;
}

export interface EsisEorGenerationResponse {
  documentsId: string;
  typesGenerated: GeneratedLendingDocumentType[];
  rawHtmlDocuments: {
    documentType: GeneratedLendingDocumentType;
    html: string;
  }[];
}

export interface ProcFeeRateViewModel {
  rateId: number;
  productName: string;
  productCode: string | null;
  grossPaymentPercent: number;
  grossPaymentAdditional: number;
  grossPaymentMin: number | null;
  grossPaymentMax: number | null;
  netPaymentPercent: number | null;
  netPaymentMin: number | null;
  netPaymentMax: number | null;
  isMatched: boolean;
}

export interface ProcFeePaymentRouteViewModel {
  paymentRouteName: string;
  rates: ProcFeeRateViewModel[];
}

export interface TailoredFiltersViewModel {
  name: string;
  selected: boolean;
}

export interface EsisSearchDetailsModel {
  lendingTypeId: number | null;
  lendingTypeCode: string | null;
  applicants?: {
    applicantNumber: number;
    dateOfBirth: Date | null;
    countryResident?: ApplicantCountry | null;
    grossAnnualIncome?: number | null;
    taxStatusBtl?: TaxStatusBtl | null;
  }[];
  mortgageType: MortgageType;
  remortgageType: RemortgageType;
  productTypeExtended: ProductType;
  btlType?: BtlType;
  newBuild?: boolean;
  helpToBuy?: boolean;
  limitedCompanyPurchase?: boolean;
  portfolioLandlord?: boolean;
  purchaserType: PurchaserType;
  propertyValue: number;
  purchasePriceShare?: number;
  loanAmount: number | null;
  interestOnlyAmount: number;
  outstandingBalanceCurrentMortgage: number;
  mortgageTerm: { years: number | undefined; months: number | undefined };
  repaymentMethod: { value: RepaymentMethod | undefined };
  location: UkLocation | null | undefined;
  propertyType: PropertyType | null;
  propertyDetails?: PropertyDetails;
  regulated?: boolean;
  limitedCompany?: boolean;
  methodOfRepayment?: { value: MethodOfRepayment | undefined };
  currentMortgageLender?: string;
  productTransferInitialDate?: Date | string;
  applicantsDateOfBirth?: {
    applicantNumber: number;
    dateOfBirth: Date | null;
  }[];
  discountedPurchasePrice?: number;
  additionalBorrowing?: boolean;
  capitalRaising?: boolean;
  amountTransferredFromOtherLender?: number;
  limitedCompanySpecialPurposeVehicle?: boolean;
  numberOfProperties?: number;
  firstTimeBuyer?: number;
  firstTimeLandlord?: number;
  ownerOccupier?: number;
  rentalAmountReceivedExpected?: number;
  holidayLetIncomeLow?: number;
  holidayLetIncomeMedium?: number;
  holidayLetIncomeHigh?: number;
  holidayLetIncomeFrequency?: HolidayLetFrequency;
  numberOfBedrooms?: number;
  numberOfBlocks?: number;
  propertySelfContainedFlats?: boolean;
}

export interface EsisEorGenFormDetails {
  fees: CostsToBePaidCustom[];
  procFeePaymentRates: ProcFeePaymentRouteViewModel[];
  requiredAttributes: string[];
  brokerFirmName: string;
  userEsisInformation: DocumentPreferences;
  requiresMoreDetails: boolean;
  filters: TailoredFiltersViewModel;
}

export interface LendingDocumentInformationFormType {
  lendingTypeId: number;
  documentType: LendingDocumentInformationType;
}

export interface IgnitePlusCallbackDocument {
  filename: string;
  documentType: string;
  url: string;
}

export interface ICustomFee {
  value: number;
  name: string;
}

export interface EsisPreviewFormModel {
  mortgageType: MortgageType;
  propertyValue: number;
  loanAmount: number;
  ioLoanAmount: number | null;
  repaymentMethod: RepaymentMethod;
  mortgageTermYears: number;
  mortgageTermMonths: number;
}

interface UniqueFeeCombinationValidatorFee {
  lendingTypeId: number | null;
  mortgageTypes: MortgageTypeValues[] | null;
  whenPayable: WhenPayableCustom | null;
  whenRefundable: WhenRefundable | null;
}

interface FeeCombinationPermutation {
  lendingTypeId: number;
  mortgageType: MortgageTypeValues;
  whenPayable: number;
  whenRefundable: number;
}

/**
 * Shared validator for validating that broker fees do not overlap.
 * Any fees with the same 'lending type', 'mortgage type', 'when payable', and 'when refundable' are considered non-unique.
 */
export function uniqueFeeCombinationValidator({ fees, lendingTypeOptions, mortgageTypeOptions, whenPayableOptions, whenRefundableOptions }: {
  fees: UniqueFeeCombinationValidatorFee[];
  lendingTypeOptions: { value: number | null; label: string }[];
  mortgageTypeOptions: { value: MortgageTypeValues; label: string }[];
  whenPayableOptions: { value: number; label: string }[];
  whenRefundableOptions: { value: number; label: string }[];
}): ValidationErrors | null {

  if (!fees) {
    return null;
  }

  // Expands a fee into all permutations of that fee. E.G. { lendingType: null, mortgageTypes: [Purchase, Remortgage] } is 4 permutations:
  // Resi+Purchase, Resi+Remortgage, BTL+Purchase and BTL+Remortgage.
  function* getPermutations(fee: UniqueFeeCombinationValidatorFee) {
    if (!fee.whenPayable || !fee.whenRefundable || !fee.mortgageTypes) {
      return;
    }

    for (const mortgageType of fee.mortgageTypes) {
      if (fee.lendingTypeId === null) {
        for (const lendingType of lendingTypeOptions) {
          if (typeof lendingType.value === 'number') {
            yield { lendingTypeId: lendingType.value, mortgageType, whenPayable: +fee.whenPayable, whenRefundable: +fee.whenRefundable };
          }
        }
      } else {
        yield { lendingTypeId: +fee.lendingTypeId, mortgageType, whenPayable: +fee.whenPayable, whenRefundable: +fee.whenRefundable };
      }
    }
  }

  // Loop through all permutations and see if a duplicate is found.
  const seenPermutations = new Set<string>();
  const duplicatePermutations = new Map<string, FeeCombinationPermutation>();

  for (const fee of fees) {
    for (const permutation of getPermutations(fee)) {
      const key = `${permutation.lendingTypeId}|${permutation.mortgageType}|${permutation.whenPayable}|${permutation.whenRefundable}`;
      if (seenPermutations.has(key)) {
        duplicatePermutations.set(key, permutation);
      } else {
        seenPermutations.add(key);
      }
    }
  }

  // No duplicates, no error
  if (!duplicatePermutations.size) {
    return null;
  }

  // Attempt to group the duplicates together where possible to reduce the amount of error messages
  // E.G. a duplicate on resi purchase and btl purchase should show message as a 'purchase on all lending types'
  const errorMessages = new Set<string>();

  // Group by 'when payable' and 'when refundable'
  const duplicatesByWhenPayableRefundable = groupBy(duplicatePermutations.values(), c => `${c.whenPayable}|${c.whenRefundable}`);

  for (const whenGroup of duplicatesByWhenPayableRefundable.values()) {
    const whenPayableName = whenPayableOptions.find(x => x.value === whenGroup[0].whenPayable)?.label ?? '';
    const whenRefundableName = whenRefundableOptions.find(x => x.value === whenGroup[0].whenRefundable)?.label ?? '';

    // For each group, do a 'merged group' against the values' lending types, using the mortgage type as a value comparer
    // This will give us a list of one or more lending types against one or more mortgage types, with a fairly small footprint, and then we
    // build the error message from that.
    for (const typeGroup of mergedGroupBy(whenGroup, g => g.lendingTypeId, (a, b) => a.mortgageType === b.mortgageType)) {
      const lendingTypeName = typeGroup.keys.length === 2
        ? 'all lending types'
        : lendingTypeOptions.find(x => x.value === typeGroup.keys[0])?.label.toLowerCase() ?? '';

      const mortgageTypeNames = join2(
        [...new Set(typeGroup.values.map(x => mortgageTypeOptions.find(y => y.value === x.mortgageType)?.label ?? ''))],
        ', ', ' and ')
        .toLowerCase();

      errorMessages.add(`There are multiple fees for ${lendingTypeName} ${mortgageTypeNames} with payable '${whenPayableName}' and refundable '${whenRefundableName}'.`);
    }
  }

  return { uniqueFeeCombinationError: [...errorMessages] };
}
